/**
 * Turning values into the things a person reads.
 *
 * ── Why this is a module and not a `toLocaleString()` at each call site ──
 *
 * Because that is what the portal did, and it is wrong in two directions at
 * once. `11/08/2026, 14:32:07` is unreadable in a column of forty rows — the
 * eye has to parse each one to answer "was this recent" — and it is the same
 * width and weight as a timestamp from three years ago, so nothing stands out.
 * Meanwhile a raw `2502` in a table of counts does not line up with `947` above
 * it, because proportional digits are different widths.
 *
 * The old portal had both of these right and the current one lost them. This is
 * where they come back, once, rather than as a helper per screen.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Relative for the recent past, absolute once relative stops helping.
 *
 * `3h ago` is instantly comparable to `12m ago`. `41d ago` is not instantly
 * anything — by the time something is a week old the useful question is "which
 * day", so the format switches to a date rather than counting ever upward.
 *
 * `Yesterday, 09:10` keeps the time, because within a day or two the hour is
 * still part of the answer.
 *
 * Always in the viewer's own timezone. An admin reading an audit trail needs
 * "when was this, for me" — converting from UTC in their head is how the wrong
 * event gets blamed.
 */
export function ago(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  // An unparseable value renders as what the server actually sent. "Invalid
  // Date" tells the reader nothing they can act on.
  if (Number.isNaN(d.getTime())) return String(iso);

  const delta = now - d.getTime();

  /**
   * A FUTURE timestamp is shown as an absolute date rather than "in 3 days".
   *
   * Almost everything this portal renders is a record of something that already
   * happened, so a future one is either clock skew or a scheduled effective
   * date. Both are better served by the actual date than by a countdown that
   * reads as if the event has occurred.
   */
  if (delta < -MINUTE) return absolute(d);

  if (delta < 45 * SECOND) return 'Just now';
  if (delta < HOUR) return `${Math.round(delta / MINUTE)}m ago`;
  if (delta < DAY) return `${Math.round(delta / HOUR)}h ago`;

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const yesterday = new Date(now - DAY);
  if (sameDay(d, yesterday)) return `Yesterday, ${time(d)}`;
  if (delta < 7 * DAY) return `${Math.round(delta / DAY)}d ago`;

  return absolute(d);
}

/** The full value, for a `title` attribute. Relative time is easier to read and
 *  strictly less precise, so the exact moment stays one hover away rather than
 *  being lost. */
export function exact(iso: string | Date | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString();
}

/**
 * A moment as a person reads it: "Oct 24, 7:34 AM", with the year only when it
 * is not this one. For records that need the time of day and not the seconds,
 * where `ago` would be too vague (Reports' case file, support#87).
 */
export function stamp(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${absolute(d)}, ${time(d)}`;
}

function absolute(d: Date): string {
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    // The year is dropped for the current one. In a list where almost every row
    // is this year, repeating it on all of them hides the rows where it differs.
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function time(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Thousands separators, in the viewer's locale. `2502` and `2,502` are the
 *  same number and only one of them is read correctly at a glance. */
export function count(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—';
}

/**
 * Applied to anything that renders a column of figures.
 *
 * Proportional digits are different widths, so `1,847` and `2,502` do not line
 * up under each other and a column of them looks ragged. Tabular numerals are
 * what the typeface ships for exactly this.
 */
export const numeric = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * `812 kB`, `4.2 MB`. Decimal units, not binary.
 *
 * `MiB` is the honest unit for something Postgres reports in bytes, and it is
 * the wrong one here: this sits beside a row count on a screen read at a glance,
 * and a unit that invites a conversion is a unit nobody reads. The number is an
 * order of magnitude, not an invoice.
 *
 * One decimal below ten and none above, so a column of them stays the same
 * width without `4.0 GB` implying a precision the estimate behind it lacks.
 */
export function bytes(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)} B`;

  const units = ['kB', 'MB', 'GB', 'TB'];
  let v = n / 1000;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** `12ms`, `1.4s`. Latency is the one number here where the unit changes the
 *  meaning, so it travels with the value rather than living in a column header. */
export function duration(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}
